import {faPortrait} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import Helmet from 'react-helmet';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {dateFormatFR, numberFormatThousands} from '../../../utils';
import DataTable from '../../../common/components/Datatable/Datatable';
import Screen from '../../../common/components/layout/Screen';
import {FILTER_EXPRESSION_OPERATOR, FILTER_OPERATOR} from '../../../common/constants';
import {GRAPHQL_QUERY_MERCHANTS} from '../../../common/queries/merchants';
import AddMerchant from './AddMerchant';
import MerchantRowAction from './MerchantRowAction';
import config from '../../../config/app';
import DisplayBoolean from '../../../common/components/DisplayBoolean';

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const renderMetaData = t => (
  <Helmet
    title={`${config.name} - ${t('title')}`}
    meta={[{name: 'description', content: `${config.name} - ${t('meta')}`}]}
  />
);

const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('brand'),
    accessor: 'brand.name',
    sortable: true,
  },
  {
    Header: t('firstName'),
    accessor: 'user.firstName',
    sortable: true,
  },
  {
    Header: t('lastName'),
    accessor: 'user.lastName',
    sortable: true,
  },
  {
    Header: t('businessName'),
    accessor: 'businessName',
    sortable: true,
  },
  {
    Header: t('email'),
    accessor: 'contactEmail',
    sortable: true,
  },
  {
    Header: t('creation'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'createdAt',
    sortable: true,
  },
  {
    Header: t('notifications'),
    Cell: ({cell}) => numberFormatThousands(cell.value),
    accessor: 'totalAvailableNotification',
    sortable: true,
  },
  {
    Header: t('active'),
    Cell: ({cell}) => <DisplayBoolean bool={cell.value}/>,
    accessor: 'user.isActive',
  },
  {
    Header: t('subscription'),
    Cell: ({cell}) => <DisplayBoolean bool={cell.value}/>,
    accessor: 'user.hasActiveSubscription',
  },
  {
    Header: t('action'),
    Cell: displayAction,
    accessor: row => row,
  },
];

const displayAction = ({cell}) => <MerchantRowAction {...cell.value}/>;

const initialFilters = {
  relations: ['user', 'brand'],
};

function MerchantView({t}) {
  const [advancedFilters, setAdvancedFilters] = useState(initialFilters);
  const onSearch = search => setAdvancedFilters({
    operator: FILTER_EXPRESSION_OPERATOR.Or,
    filters: [{
      field: 'merchant.id',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'user.firstName',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'user.lastName',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'contactEmail',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'businessName',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'brand.name',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }],
    relations: ['user', 'brand'],
  });

  return (
    <Screen
      header={renderMetaData(t)}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faPortrait} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('merchants')}</Span>
        </>
      }
      rightTitle={<AddMerchant/>}>
      <DataTable
        columns={columns(t)}
        keyResult="merchants"
        query={GRAPHQL_QUERY_MERCHANTS}
        onSearch={onSearch}
        advancedFilters={advancedFilters}
        fetchPolicy={'cache-and-network'}
      />
    </Screen>
  );
}

MerchantView.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('merchant')(MerchantView);
